export interface PrintOptions {
    elementId: string;
    title?: string;
    customStyles?: string;
    showPrintDialog?: boolean;
}

/**
 * Collects all stylesheets and style tags from document head
 */
const collectHeadStyles = (): string => {
    try {
        const nodes = document.head ? Array.from(document.head.querySelectorAll('link[rel="stylesheet"], style')) : [];
        return nodes.map((n) => n.outerHTML).join("\n");
    } catch (err) {
        return "";
    }
};

/**
 * Default print styles
 */
const getPrintStyles = (): string => {
    return `
        @page { margin: 12mm; }
        html, body {
            background: white;
        }
        body {
            margin: 0;
            padding: 20px;
            color: black;
            font-family: system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji";
        }

        * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }

        .print-content {
            max-width: 600px;
            margin: 0 auto;
        }

        .print\\:hidden { display: none !important; }
    `;
};

/**
 * Builds HTML for print iframe
 */
const buildPrintHTML = (element: HTMLElement, title: string, customStyles: string): string => {
    const headLinksAndStyles = collectHeadStyles();
    const printStyles = getPrintStyles();
    const currentTheme = document.documentElement.getAttribute("data-theme");
    const themeAttribute = currentTheme ? `data-theme="${currentTheme}"` : "";

    return `<!DOCTYPE html>
        <html ${themeAttribute}>
        <head>
            <title>${title}</title>
            <style>${printStyles}</style>
            ${headLinksAndStyles}
            <style>${customStyles}</style>
        </head>
        <body>
            <div class="print-content">${element.outerHTML}</div>
        </body>
        </html>`;
};

/**
 * Creates and styles print iframe
 */
const createPrintIframe = (): HTMLIFrameElement => {
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    iframe.style.visibility = "hidden";
    document.body.appendChild(iframe);
    return iframe;
};

/**
 * Loads HTML content into iframe
 */
const loadIframeContent = (iframe: HTMLIFrameElement, html: string): void => {
    try {
        (iframe as any).srcdoc = html;
    } catch {
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (doc) {
            doc.open();
            doc.write(html);
            doc.close();
        }
    }
};

/**
 * Sets up print dialog and cleanup
 */
const setupPrintDialog = (iframe: HTMLIFrameElement): void => {
    iframe.onload = () => {
        const w = iframe.contentWindow;
        if (!w) return;

        const cleanup = () => setTimeout(() => iframe.parentNode?.removeChild(iframe), 0);

        try {
            w.addEventListener("afterprint", cleanup as any);
        } catch {
            setTimeout(cleanup, 1200);
        }

        try {
            w.focus();
            w.print();
        } catch {
            cleanup();
        }
    };
};

export const printElement = (options: PrintOptions): void => {
    const { elementId, title = "Print", customStyles = "", showPrintDialog = true } = options;

    const elementToPrint = document.getElementById(elementId);

    if (!elementToPrint) {
        console.error("Element not found:", elementId);
        return;
    }

    const html = buildPrintHTML(elementToPrint, title, customStyles);
    const iframe = createPrintIframe();

    loadIframeContent(iframe, html);

    if (showPrintDialog) {
        setupPrintDialog(iframe);
    }
};
